/**
 * Clip renaming: "<number> - <tag> + <tag>.<ext>".
 *
 * The number is the original filename's digits ("IMG_777" → "777"), keeping
 * a leading "E" for iPhone edited clips ("IMG_E777" → "E777"). Very long digit
 * runs — date-stamped names like "DJI_20260930_111742_920_video.MP4" — are cut
 * to their last 4 digits ("2920") so names stay short.
 */

/** Digit runs longer than this are shortened. Keeps GoPro-style "010123" whole. */
const LONG_DIGITS = 8;
const SHORT_DIGITS = 4;

/** `minDigits` widens the shortened number (used to break name clashes);
 *  Infinity keeps every digit. */
export function buildRenamedName(originalName: string, activities: string[], minDigits = SHORT_DIGITS): string {
  const dotIndex = originalName.lastIndexOf(".");
  const rawBase = dotIndex > -1 ? originalName.substring(0, dotIndex) : originalName;
  const ext = dotIndex > -1 ? originalName.substring(dotIndex) : "";
  // Strip non-digit characters from the stem, but preserve a leading "E" before digits
  // e.g. IMG_E777 → E777, IMG_777 → 777, IMG_e2481 → E2481
  // Fall back to the original stem only if stripping leaves nothing at all
  const eMatch = rawBase.match(/[Ee](\d+)$/);
  let digits = rawBase.replace(/[^0-9]/g, "");
  if (digits.length > LONG_DIGITS) digits = digits.slice(-Math.max(SHORT_DIGITS, minDigits));
  const base = eMatch ? `E${eMatch[1]}` : digits || rawBase;
  if (!activities || activities.length === 0) return `${base}${ext}`;
  return `${base} - ${activities.join(" + ")}${ext}`;
}

/**
 * Pick a name for each clip that's unique within its destination folder
 * (case-insensitive — macOS and Drive users treat "A.mp4" and "a.mp4" alike).
 * On a clash the number is widened (last 6 → 8 → all digits) before falling
 * back to " (2)", " (3)"… so two DJI clips ending in "…920" don't overwrite
 * each other. `taken` holds names already used in each folder, keyed by folder.
 */
export function assignUniqueNames(
  clips: Array<{ key: number; folder: string; originalName: string; activities: string[]; currentName?: string }>,
  taken: Map<string, Set<string>> = new Map(),
): Map<number, string> {
  const out = new Map<number, string>();
  for (const c of clips) {
    let used = taken.get(c.folder);
    if (!used) taken.set(c.folder, (used = new Set()));
    const candidates = [
      c.currentName || buildRenamedName(c.originalName, c.activities),
      buildRenamedName(c.originalName, c.activities, 6),
      buildRenamedName(c.originalName, c.activities, 8),
      buildRenamedName(c.originalName, c.activities, Infinity),
    ];
    let name = candidates.find((n) => !used.has(n.toLowerCase()));
    if (!name) name = withCopyNumber(candidates[candidates.length - 1]!, (n) => used.has(n.toLowerCase()));
    used.add(name.toLowerCase());
    out.set(c.key, name);
  }
  return out;
}

/** "2920 - Gait.mp4" → "2920 - Gait (2).mp4", counting up until `isTaken` is false. */
export function withCopyNumber(name: string, isTaken: (n: string) => boolean): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 2; ; i++) {
    const candidate = `${stem} (${i})${ext}`;
    if (!isTaken(candidate)) return candidate;
  }
}

/** Re-tag an already-named clip while keeping its number (which may have been
 *  widened to break a clash): "2920 - Old.mp4" + ["New"] → "2920 - New.mp4". */
export function replaceActivities(currentName: string, activities: string[]): string {
  const dot = currentName.lastIndexOf(".");
  const stem = dot > 0 ? currentName.slice(0, dot) : currentName;
  const ext = dot > 0 ? currentName.slice(dot) : "";
  const sep = stem.indexOf(" - ");
  const base = sep >= 0 ? stem.slice(0, sep) : stem;
  return activities.length ? `${base} - ${activities.join(" + ")}${ext}` : `${base}${ext}`;
}
